import api from './api';
import { LoggerService } from './LoggerService';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserResponse {
  user: User;
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<any> {
    if (!credentials.username || !credentials.password) {
      throw new Error('Username and password are required');
    }

    LoggerService.debug('Login attempt', { username: credentials.username });

    const form = new URLSearchParams();
    form.append('username', credentials.username);
    form.append('password', credentials.password);

    const response = await api.post('/auth/login-step1', form.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  }

  async register(userData: RegisterData): Promise<User> {
    const response = await api.post('/auth/register', userData);
    return response.data;
  }

  async getCurrentUser(token?: string): Promise<User> {
    LoggerService.debug('getCurrentUser called');

    if (token) {
      const axios = (await import('axios')).default;
      const baseUrl = '/api/v1';
      const response = await axios.get(`${baseUrl}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      LoggerService.debug('getCurrentUser response received');
      return response.data;
    } else {
      const response = await api.get('/auth/me');
      LoggerService.debug('getCurrentUser response received');
      return response.data;
    }
  }

  async logout(): Promise<void> {
    await api.post('/auth/logout');
  }

  async refreshToken(): Promise<AuthResponse> {
    const response = await api.post('/auth/refresh');
    return response.data;
  }

  async updateProfile(profileData: Partial<User>): Promise<User> {
    const response = await api.put('/users/me', profileData);
    return response.data;
  }

  async demoLogin(): Promise<never> {
    LoggerService.error('demoLogin() called - method removed for security');
    throw new Error('Demo auto-login has been disabled for security. Please use the login modal.');
  }

  async verifyTwoFactor(code: string, tempToken: string): Promise<any> {
    try {
      LoggerService.debug('Verifying 2FA code');
      const response = await api.post('/auth/login-step2', {
        code,
        temp_token: tempToken,
      });
      LoggerService.debug('2FA verification successful');
      return response.data;
    } catch (error: any) {
      LoggerService.error('2FA verification failed', error?.message);
      throw new Error(error.response?.data?.detail || '2FA verification failed');
    }
  }
}

export const authService = new AuthService();
